import React, { useEffect } from 'react';
import { Modal, Form, Button, Card, Row, Col, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faCheck, faPills, faPlus, faExternalLinkAlt, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useForm, Controller } from 'react-hook-form';
import { useAtualizarLote } from '../../../../hooks/useSngpc';
import { EstoqueInventarioType } from '../../../../data/sngpc';
import LinkProduto from '../../../../components/common/LinkProduto';

interface ModalEditarProdutoProps {
    show: boolean;
    onHide: () => void;
    produto: EstoqueInventarioType | null;
    inventarioId?: number;
    onAdicionarLote?: (produto: EstoqueInventarioType) => void;
}

interface FormData {
    quantidade: number;
    rms: string;
    // Apenas classe terapêutica e unidade para o lote (não atualiza cadastro)
    classeTerapeutica: string;
    unidade: string;
}

const ModalEditarProduto: React.FC<ModalEditarProdutoProps> = ({ show, onHide, produto, inventarioId, onAdicionarLote }) => {
    const atualizarLoteMutation = useAtualizarLote();

    const { control, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
        defaultValues: {
            quantidade: 0,
            rms: '',
            classeTerapeutica: '',
            unidade: '0' // Padrão para Caixa
        }
    });

    // Resetar formulário quando o produto mudar
    useEffect(() => {
        if (produto && show) {
            reset({
                quantidade: produto.quantidade || 0,
                rms: produto.rms || '',
                classeTerapeutica: produto.classeTerapeutica?.toString() || '',
                unidade: produto.unidade?.toString() || '0' // Padrão para Caixa
            });
        }
    }, [produto, show, reset]);

    const onSubmit = async (data: FormData) => {
        if (!produto?.loteId) return;

        try {
            await atualizarLoteMutation.mutateAsync({
                loteId: produto.loteId,
                numero: produto.numero || produto.lote || '',
                rms: data.rms,
                novaQuantidade: data.quantidade,
                classeTerapeutica: data.classeTerapeutica ? parseInt(data.classeTerapeutica) : undefined,
                unidade: data.unidade ? parseInt(data.unidade) : 0
            });
            onHide();
        } catch (error) {
            console.error('Erro ao atualizar produto:', error);
        }
    };

    if (!produto) return null;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon icon={faEdit} className="me-2 text-primary-emphasis" />
                    Editar Produto no Inventário
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Body>
                    {/* Informações do Produto */}
                    <Card className="mb-4">
                        <Card.Header>
                            <h6 className="mb-0">
                                <FontAwesomeIcon icon={faPills} className="me-2" />
                                Informações do Produto
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={8}>
                                    <LinkProduto produtoId={produto.produtoId}>
                                        <strong>{produto.nomeProduto || 'Produto sem descrição'}</strong>
                                    </LinkProduto>
                                    <div className="text-body-secondary small">
                                        Código: {produto.produtoCodigo || produto.produtoId}
                                        {produto.produtoCodigoBarras && ` | EAN: ${produto.produtoCodigoBarras}`}
                                    </div>
                                    {produto.fabricante && (
                                        <div className="text-body-secondary small">
                                            Fabricante: {produto.fabricante}
                                        </div>
                                    )}
                                </Col>
                                <Col md={4} className="text-end">
                                    <div className="text-body-secondary">
                                        Lote: <span className="badge bg-secondary">{produto.numero || produto.lote || 'Sem lote'}</span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Formulário de Edição */}
                    <Card>
                        <Card.Header>
                            <h6 className="mb-0">
                                <FontAwesomeIcon icon={faEdit} className="me-2" />
                                Editar Informações
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Quantidade *</Form.Label>
                                        <Controller
                                            name="quantidade"
                                            control={control}
                                            rules={{
                                                required: 'Quantidade é obrigatória',
                                                min: { value: 0, message: 'Quantidade não pode ser negativa' }
                                            }}
                                            render={({ field }) => (
                                                <Form.Control
                                                    {...field}
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    placeholder="0"
                                                    isInvalid={!!errors.quantidade}
                                                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                                />
                                            )}
                                        />
                                        {errors.quantidade && (
                                            <Form.Control.Feedback type="invalid">
                                                {errors.quantidade.message}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>RMS *</Form.Label>
                                        <Controller
                                            name="rms"
                                            control={control}
                                            rules={{ required: 'RMS é obrigatório' }}
                                            render={({ field }) => (
                                                <Form.Control
                                                    {...field}
                                                    type="text"
                                                    placeholder="Registro no Ministério da Saúde"
                                                    isInvalid={!!errors.rms}
                                                />
                                            )}
                                        />
                                        {errors.rms && (
                                            <Form.Control.Feedback type="invalid">
                                                {errors.rms.message}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Informações do Lote */}
                    <Card className="mt-4">
                        <Card.Header>
                            <h6 className="mb-0">
                                <FontAwesomeIcon icon={faEdit} className="me-2" />
                                Informações do Lote
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Classe Terapêutica *</Form.Label>
                                        <Controller
                                            name="classeTerapeutica"
                                            control={control}
                                            rules={{ required: 'Classe Terapêutica é obrigatória' }}
                                            render={({ field }) => (
                                                <Form.Select {...field} isInvalid={!!errors.classeTerapeutica}>
                                                    <option value="">Selecione...</option>
                                                    <option value="0">Antimicrobiano</option>
                                                    <option value="1">Sujeito a controle especial</option>
                                                </Form.Select>
                                            )}
                                        />
                                        {errors.classeTerapeutica && (
                                            <Form.Control.Feedback type="invalid">
                                                {errors.classeTerapeutica.message}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Unidade *</Form.Label>
                                        <Controller
                                            name="unidade"
                                            control={control}
                                            rules={{ required: 'Unidade é obrigatória' }}
                                            render={({ field }) => (
                                                <Form.Select {...field} isInvalid={!!errors.unidade}>
                                                    <option value="0">Caixa</option>
                                                    <option value="1">Frasco</option>
                                                </Form.Select>
                                            )}
                                        />
                                        {errors.unidade && (
                                            <Form.Control.Feedback type="invalid">
                                                {errors.unidade.message}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Alert variant="info" className="border border-info border-opacity-25 bg-info-subtle mb-0">
                                <div className="text-info-emphasis">
                                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                    <strong>Informação:</strong> Essas informações são específicas do lote e não alteram o cadastro geral do produto.
                                </div>
                            </Alert>
                        </Card.Body>
                    </Card>
                </Modal.Body>
                <Modal.Footer>
                    <div className="d-flex justify-content-between w-100">
                        <div>
                            {onAdicionarLote && produto && (
                                <Button
                                    variant="outline-success"
                                    onClick={() => {
                                        onAdicionarLote(produto);
                                        onHide();
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                    Adicionar Novo Lote
                                </Button>
                            )}
                        </div>
                        <div className="d-flex gap-2">
                            <Button variant="secondary" onClick={onHide}>
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={atualizarLoteMutation.isPending}
                            >
                                <FontAwesomeIcon icon={faCheck} className="me-2" />
                                {atualizarLoteMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ModalEditarProduto; 